/* ==========================================================================
   ATN Homepage V1.0 — Section 03: Sponsor Strip
   Spec reference: 03_Section.docx §9 (carousel), §16 (interaction), §21 (analytics)

   - Continuous marquee: logos scroll seamlessly and never leave a gap.
   - Pauses on hover / keyboard focus (handled in CSS).
   - Clones the logo set enough times to fill the viewport, then sets the
     animation duration from a pixels-per-second speed so the visual speed
     stays constant no matter how many logos are added.
   - Arrows toggle scroll direction (forward / reverse).
   - Respects prefers-reduced-motion (marquee disabled, static row shown).
   - Optional analytics on sponsor and CTA clicks.
   ========================================================================== */

(function () {
  'use strict';

  var reducedMotion =
    window.matchMedia &&
    window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  function deviceType() {
    var w = window.innerWidth;
    if (w <= 749) return 'mobile';
    if (w <= 1100) return 'tablet';
    return 'desktop';
  }

  function sectionIdFrom(el) {
    var wrapper = el.closest('[id^="shopify-section-"]');
    return wrapper ? wrapper.id.replace('shopify-section-', '') : '';
  }

  function send(root, eventName, params) {
    if (root.dataset.analytics !== 'true' || !eventName) return;

    var payload = Object.assign(
      {
        device_type: deviceType(),
        section_id: sectionIdFrom(root),
        current_page: window.location.pathname
      },
      params || {}
    );

    if (Array.isArray(window.dataLayer)) {
      window.dataLayer.push(Object.assign({ event: eventName }, payload));
    }
    if (typeof window.gtag === 'function') {
      window.gtag('event', eventName, payload);
    }
  }

  // Strip theme-editor / id attributes from cloned nodes so they never register
  // as real Shopify blocks or create duplicate element ids, and keep them out
  // of the tab order / assistive tech.
  function sanitizeClone(node) {
    var nodes = [node].concat(
      Array.prototype.slice.call(node.querySelectorAll('*'))
    );
    nodes.forEach(function (n) {
      if (!n.attributes) return;
      Array.prototype.slice.call(n.attributes).forEach(function (attr) {
        if (attr.name.indexOf('data-shopify') === 0 || attr.name === 'id') {
          n.removeAttribute(attr.name);
        }
      });
    });
    node.setAttribute('aria-hidden', 'true');
    node.setAttribute('data-atn-ss-clone', 'true');
    Array.prototype.slice
      .call(node.querySelectorAll('a, button, [tabindex]'))
      .forEach(function (el) {
        el.setAttribute('tabindex', '-1');
      });
  }

  function Marquee(root) {
    this.root = root;
    this.viewport = root.querySelector('[data-atn-ss-viewport]');
    this.track = root.querySelector('[data-atn-ss-track]');
    this.prevBtn = root.querySelector('[data-atn-ss-prev]');
    this.nextBtn = root.querySelector('[data-atn-ss-next]');
    if (!this.track) return;

    this.speed = parseFloat(root.dataset.speed) || 40; // pixels per second
    this.autoplay = root.dataset.autoplay === 'true' && !reducedMotion;

    this.bindArrows();
    this.bindAnalytics();

    if (this.autoplay) {
      this.build();
      this.bindResize();
    }
  }

  Marquee.prototype.originals = function () {
    return Array.prototype.slice.call(
      this.track.querySelectorAll('[data-atn-ss-logo]:not([data-atn-ss-clone])')
    );
  };

  Marquee.prototype.clearClones = function () {
    Array.prototype.slice
      .call(this.track.querySelectorAll('[data-atn-ss-clone]'))
      .forEach(function (c) {
        c.remove();
      });
  };

  Marquee.prototype.build = function () {
    var originals = this.originals();
    if (!originals.length) return;

    this.clearClones();
    // Enable marquee layout (single non-wrapping line) before measuring width.
    this.root.setAttribute('data-atn-ss-ready', 'true');

    var setWidth = this.track.scrollWidth;
    if (!setWidth) return;

    var viewWidth = this.viewport
      ? this.viewport.clientWidth
      : window.innerWidth;

    // Copies of the original set needed to cover one full viewport width.
    var k = Math.max(1, Math.min(8, Math.ceil(viewWidth / setWidth)));

    // The track holds 2 * k identical copies so translateX(-50%) loops
    // seamlessly. One copy already exists, so append (2k - 1) clones.
    var clonesNeeded = 2 * k - 1;
    var fragment = document.createDocumentFragment();
    for (var i = 0; i < clonesNeeded; i++) {
      originals.forEach(function (logo) {
        var clone = logo.cloneNode(true);
        sanitizeClone(clone);
        fragment.appendChild(clone);
      });
    }
    this.track.appendChild(fragment);

    // Duration = distance of one half (k sets) / speed (pixels per second).
    var duration = (k * setWidth) / this.speed;
    this.track.style.setProperty(
      '--atn-ss-duration',
      duration.toFixed(2) + 's'
    );
  };

  Marquee.prototype.bindResize = function () {
    var self = this;
    this._onResize = function () {
      clearTimeout(self._resizeTimer);
      self._resizeTimer = setTimeout(function () {
        self.build();
      }, 200);
    };
    window.addEventListener('resize', this._onResize);
  };

  Marquee.prototype.setDirection = function (dir) {
    this.track.style.animationDirection = dir;
  };

  Marquee.prototype.bindArrows = function () {
    var self = this;
    if (this.prevBtn) {
      this.prevBtn.addEventListener('click', function () {
        self.setDirection('reverse');
      });
    }
    if (this.nextBtn) {
      this.nextBtn.addEventListener('click', function () {
        self.setDirection('normal');
      });
    }
  };

  Marquee.prototype.bindAnalytics = function () {
    var self = this;
    if (this.root.dataset.analytics !== 'true') return;

    this.root.addEventListener('click', function (event) {
      var sponsor = event.target.closest('[data-atn-ss-sponsor]');
      if (sponsor) {
        send(self.root, 'homepage_sponsor_logo_click', {
          sponsor_name: sponsor.dataset.atnSsSponsor || '',
          destination_url: sponsor.getAttribute('href') || ''
        });
        return;
      }
      var cta = event.target.closest('[data-atn-ss-event]');
      if (cta) {
        send(self.root, cta.dataset.atnSsEvent, {
          cta_label: (cta.textContent || '').trim(),
          destination_url: cta.getAttribute('href') || ''
        });
      }
    });
  };

  function init(scope) {
    (scope || document)
      .querySelectorAll('[data-atn-ss-root]')
      .forEach(function (root) {
        if (root.dataset.atnSsBound === 'true') return;
        root.dataset.atnSsBound = 'true';
        new Marquee(root);
      });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();